import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { delay, finalize, tap } from 'rxjs/operators';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { DialogServiceWrapper } from '../../services/dialog.service';
import { responseStatus } from '../constants/responseStatus.const';
import { BaseClass } from '../../shared/components/class/baseClass';
import { commonResponse } from '../constants/keys.constant';

/**
 * URLs that should NOT trigger the global full-page loader.
 * Keep this list in sync with the `excludeRegexp` array in NgxUiLoaderHttpModule (app.config.ts).
 */
const SILENT_URL_PATTERNS: RegExp[] = [
  /auth\/setings\//,   // app theme / logo / name — called at init on every page
  /auth\/user-edit\//,  // user profile — called at init and after profile save
  /global-search\//,   // global search — uses its own in-component apiLoading spinner
  /insight-admin\//,
];

@Injectable({
  providedIn: 'root',
})
export class GetInterceptor extends BaseClass implements HttpInterceptor {
  private readonly recentErrors = new Set<string>();
  private readonly errorTimeout = 5000;
  private sessionExpiryInProgress = false;
  private sessionExpiryResetMs = 5000;
  private readonly ngxLoader = inject(NgxUiLoaderService);
  private readonly dialogWrapper = inject(DialogServiceWrapper);
  constructor() {
    super();
  }

  /** Returns true when the request URL matches any silent pattern. */
  private isSilent(url: string): boolean {
    return SILENT_URL_PATTERNS.some((pattern) => pattern.test(url));
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<commonResponse>> {
    const silent = this.isSilent(req.url);
    if (!silent) {
      this.ngxLoader.start();
    }
    return next.handle(req).pipe(
      delay(0),
      tap({
        next: (event: HttpEvent<commonResponse>) => {
          if (event instanceof HttpResponse) {
            this.handleResponse(event.body as commonResponse, req.method);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error);
        },
      }),
      finalize(() => {
        if (!silent) {
          this.ngxLoader.stop();
        }
      }),
    );
  }

  private handleResponse(body: commonResponse, method: string): void {
    if (body?.status) {
      switch (body.status) {
        case responseStatus.SUCCESS:
          if (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
            this._toasterService.showSuccess(body.message);
          }
          break;
        case responseStatus.BADREQUEST:
        case responseStatus.UNAUTHORIZED:
        case responseStatus.FORBIDDEN:
        case responseStatus.REQUESTTIMEOUT:
        case responseStatus.INTERNALSERVERERROR:
          this._toasterService.showError(body.message);
          if (
            body.status === responseStatus.UNAUTHORIZED ||
            body.status === responseStatus.FORBIDDEN
          ) {
            this.dialogWrapper.closeDialog();
            this.router.navigate(['/auth/login']);
            localStorage.clear();
          }
          break;
        default:
          break;
      }
    }
  }

  private handleError(error: HttpErrorResponse): void {
    if (error && (error.status === responseStatus.UNAUTHORIZED || error.status === responseStatus.FORBIDDEN)) {

      // gate: show only once for a burst of failing requests
      if (this.sessionExpiryInProgress) return;

      this.sessionExpiryInProgress = true;
      this.dialogWrapper.closeDialog();
      localStorage.clear();
      this._toasterService.showError('Session expired please login again');
      this.router.navigate(['/auth/login']);

      // allow again later (optional)
      setTimeout(() => (this.sessionExpiryInProgress = false), this.sessionExpiryResetMs);

      return;
    }
    const errorMessage = error?.error?.error || error?.error?.message || error?.error?.detail || error?.error?.non_field_errors[0]  || 'SomethingWentWrong';

    if (errorMessage === 'SomethingWentWrong') {
      return;
    }

    if (!this.recentErrors.has(errorMessage)) {
      this.recentErrors.add(errorMessage);
      this._toasterService.showError(errorMessage);
      setTimeout(() => this.recentErrors.delete(errorMessage), this.errorTimeout);
    }

    if (error.status === responseStatus.UNAUTHORIZED || error.status === responseStatus.FORBIDDEN) {
      this.dialogWrapper.closeDialog();
      this.router.navigate(['/auth/login'], { replaceUrl: true });
      localStorage.clear();
    }
  }
}